import { useQuery } from "@tanstack/react-query";
import FeedbackWidget from "@/components/dash/FeedbackWidget";
import { dashApi, type SourceMeta } from "@/lib/dashApi";
import { fmtRelative } from "@/lib/format";
import {
  BarList,
  EmptyState,
  ErrorState,
  GapList,
  KpiCard,
  Leaderboard,
  LoadingState,
  PageHeader,
  Panel,
  StatusPill,
} from "@/components/dash/ui";

function SourceRight({ meta }: { meta?: SourceMeta }) {
  if (!meta?.available) return null;
  return (
    <span className="flex items-center gap-2">
      <StatusPill status={meta.status ?? "unknown"} />
      <span className="text-xs text-slate-500">{fmtRelative(meta.captured_at)}</span>
    </span>
  );
}

export default function RepairPipelinePage() {
  const q = useQuery({ queryKey: ["repair-pipeline"], queryFn: dashApi.repairPipeline });
  const d = q.data;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Repair Operations Pipeline"
        description="Bookings, quotes, blocked stages, parts, collection, turnaround and BuyBack. Repair value is an operational estimate, kept separate from business sales."
        right={<SourceRight meta={d?.source} />}
      />
      {q.isLoading ? (
        <LoadingState />
      ) : q.isError ? (
        <ErrorState error={q.error} />
      ) : !d || !d.source.available ? (
        <EmptyState>No Repairs snapshot has been captured yet.</EmptyState>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-6">
            <KpiCard label="Open tickets" value={d.headline.open_tickets} unit="count" />
            <KpiCard label="Overdue" value={d.headline.overdue_tickets} unit="count" tone={d.headline.overdue_tickets ? "danger" : undefined} />
            <KpiCard label="Quotes awaiting decision" value={d.quotes?.awaiting_decision ?? null} unit="count" />
            <KpiCard label="Ready for collection" value={d.collection?.ready_now ?? null} unit="count" />
            <KpiCard label="Parts past ETA" value={d.parts?.past_eta ?? null} unit="count" tone={d.parts?.past_eta ? "danger" : undefined} />
            <KpiCard label="Bookings next 24h" value={d.bookings?.upcoming_24h ?? null} unit="count" />
          </div>

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <Panel
              title="Booking funnel (range)"
              subtitle={d.bookings ? `Conversion ${d.bookings.conversion_pct ?? "—"}% · no-show ${d.bookings.no_show_pct ?? "—"}%` : undefined}
            >
              <BarList rows={d.bookings?.by_status ?? []} unit="count" empty="No bookings created in range." />
            </Panel>
            <Panel
              title="Quote pipeline"
              subtitle={d.quotes ? `${d.quotes.expiring_48h} expiring ≤48h · median decision ${d.quotes.decision_p50_hours ?? "—"}h` : undefined}
            >
              <BarList rows={d.quotes?.by_status ?? []} unit="count" empty="No quotes recorded." />
            </Panel>
            <Panel
              title="Ticket stages (live)"
              subtitle="Ages are whole-ticket ages — stage-entry times are not event-logged by the source."
            >
              {d.stages?.stages.length ? (
                <ul className="space-y-2 text-sm">
                  {d.stages.stages.map((s) => (
                    <li key={s.label} className="flex items-center justify-between">
                      <span className="text-slate-300">{s.label}</span>
                      <span className="text-slate-200">{s.value} · oldest {s.oldest_ticket_days}d</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <EmptyState compact>No live tickets.</EmptyState>
              )}
            </Panel>
            <Panel
              title="Parts on order"
              subtitle={d.parts ? `Avg supplier lead time ${d.parts.avg_lead_days ?? "—"} day(s)` : undefined}
            >
              {d.parts ? (
                <dl className="space-y-2 text-sm">
                  <div className="flex justify-between"><dt className="text-slate-400">Open orders</dt><dd className="font-medium text-slate-200">{d.parts.open_orders}</dd></div>
                  <div className="flex justify-between"><dt className="text-slate-400">Past ETA</dt><dd className={d.parts.past_eta ? "font-medium text-rose-300" : "font-medium text-slate-200"}>{d.parts.past_eta}</dd></div>
                </dl>
              ) : (
                <EmptyState compact>Not available in this snapshot.</EmptyState>
              )}
            </Panel>
          </div>

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            <Panel
              title="Collection & payment (50B)"
              subtitle={d.collection ? `Oldest ready-for-collection ticket: ${d.collection.oldest_ticket_days} day(s)` : undefined}
            >
              {d.collection ? (
                <>
                  <dl className="space-y-2 text-sm">
                    <div className="flex justify-between"><dt className="text-slate-400">Ready now</dt><dd className="font-medium text-slate-200">{d.collection.ready_now}</dd></div>
                    <div className="flex justify-between"><dt className="text-slate-400">Collected & paid (range)</dt><dd className="font-medium text-slate-200">{d.collection.collected_paid_count} · €{d.collection.collected_paid_total.toLocaleString()}</dd></div>
                  </dl>
                  {d.collection.methods.length > 0 && <div className="mt-3"><BarList rows={d.collection.methods} unit="count" /></div>}
                </>
              ) : (
                <EmptyState compact>Not available in this snapshot.</EmptyState>
              )}
            </Panel>
            <Panel title="Turnaround & promise-kept">
              {d.turnaround ? (
                <dl className="space-y-2 text-sm">
                  <div className="flex justify-between"><dt className="text-slate-400">Promise kept</dt><dd className="font-medium text-slate-200">{d.turnaround.promise_total ? `${d.turnaround.promise_kept}/${d.turnaround.promise_total} (${d.turnaround.promise_kept_pct}%)` : "No quoted completions in range"}</dd></div>
                  <div className="flex justify-between"><dt className="text-slate-400">Avg pickup lag</dt><dd className="font-medium text-slate-200">{d.turnaround.pickup_lag_avg_days ?? "—"} day(s)</dd></div>
                </dl>
              ) : (
                <EmptyState compact>Not available in this snapshot.</EmptyState>
              )}
            </Panel>
            <Panel title="Quality / rework">
              {d.quality ? (
                <dl className="space-y-2 text-sm">
                  <div className="flex justify-between"><dt className="text-slate-400">Active warranties</dt><dd className="font-medium text-slate-200">{d.quality.warranties_active}</dd></div>
                  <div className="flex justify-between"><dt className="text-slate-400">Warranty claims</dt><dd className="font-medium text-slate-200">{d.quality.warranty_claims}</dd></div>
                  <div className="flex justify-between"><dt className="text-slate-400">Repeat-repair devices (90d)</dt><dd className={d.quality.repeat_repair_devices_90d ? "font-medium text-amber-300" : "font-medium text-slate-200"}>{d.quality.repeat_repair_devices_90d}</dd></div>
                </dl>
              ) : (
                <EmptyState compact>Not available in this snapshot.</EmptyState>
              )}
            </Panel>
          </div>

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <Panel title="Technician load (open tickets now)">
              {d.tech_load.length ? <Leaderboard rows={d.tech_load} unit="count" /> : <EmptyState compact>No assigned open tickets.</EmptyState>}
            </Panel>
            <Panel
              title="BuyBack funnel (range)"
              subtitle={d.buyback ? `Paid out: ${d.buyback.paid_count} · €${d.buyback.paid_total.toLocaleString()} (operational figure, separate from sales)` : undefined}
            >
              <BarList rows={d.buyback?.by_status ?? []} unit="count" empty="No buy-back requests in range." />
              {d.buyback && d.buyback.resale_intents.length > 0 && (
                <p className="mt-3 text-xs text-slate-500">
                  Resale intents: {d.buyback.resale_intents.map((r) => `${r.label} ${r.value}`).join(" · ")}
                </p>
              )}
            </Panel>
          </div>

          <GapList gaps={d.source.gaps} />
          <FeedbackWidget targetType="page" targetRef="/repairs/pipeline" />
        </>
      )}
    </div>
  );
}
